import { ReactElement, useEffect, useState } from "react";
import { Box, SxProps, Theme } from "@mui/material";
import { keyframes } from "@emotion/react";
import { DetailedRound } from "../../../models/round";
import { Fixture } from "../../../models/fixture";
import { Bet } from "../../../models/couponshares";
import { formatStringCurrency } from "../../../common/CurrencyFormatter";
import { getJackpot } from "../../../common/roundhelpers";
import { GetJackpotString, IsOrangeLight, themeC } from "../../../theme/themehelper";
import { getTheGameStandings, TheGameManifest, TheGameStandings, TheGameStandingsMarket, toCouponSign } from "./thegamemanifest";

export const LINE = "var(--thegame-line, rgba(127, 127, 127, .25))";
export const CORRECT = "var(--thegame-correct, #25852F)";
export const ON_CORRECT = "var(--thegame-on-correct, #FFFFFF)";
export const CORRECT_SOFT = "var(--thegame-correct-soft, rgba(37, 133, 47, .14))";
export const WRONG = "var(--thegame-wrong, #B3261E)";
export const WRONG_SOFT = "var(--thegame-wrong-soft, rgba(179, 38, 30, .10))";
export const LIVE = "var(--thegame-live, #D92D20)";

/* ------------------------------------------------------------------ */
/*  Shared styles                                                     */
/* ------------------------------------------------------------------ */

export const pageSx: SxProps<Theme> = {
  maxWidth: 560,
  margin: "0 auto",
  display: "flex",
  flexDirection: "column",
  minHeight: "100%",
  background: "var(--thegame-page-background, transparent)",
  fontFamily: "var(--font-body)",
  color: "var(--text-black)",
};

export const mainSx: SxProps<Theme> = {
  padding: "16px 16px 24px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
};

export const cardSx: SxProps<Theme> = {
  background: "var(--background_4)",
  borderRadius: "var(--radius)",
  boxShadow: "0 1px 3px rgba(23, 49, 84, .10)",
};

export const marketCardSx: SxProps<Theme> = {
  ...(cardSx as object),
  padding: "14px 16px 16px",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

export const supportiveSx: SxProps<Theme> = {
  fontFamily: "var(--font-body)",
  fontSize: 13,
  color: "var(--font-color-supportive)",
};

export const statValueSx: SxProps<Theme> = {
  fontFamily: "var(--font-body)",
  fontWeight: 700,
  color: "var(--text-black)",
};

export const sectionTitleSx: SxProps<Theme> = {
  fontFamily: "var(--font-title)",
  fontSize: 18,
  fontWeight: 700,
  textTransform: "uppercase",
};

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: .35; transform: scale(.7); }
`;

export function PulseDot({ color }: { color: string }): ReactElement {
  return (
    <Box
      component="span"
      sx={{
        width: 8,
        height: 8,
        flexShrink: 0,
        borderRadius: "50%",
        background: color,
        animation: `${pulse} 1.4s ease-in-out infinite`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
    />
  );
}

export function useCountdown(target: Date): number {
  const [left, setLeft] = useState(target.getTime() - Date.now());
  useEffect(() => {
    const id = setInterval(() => setLeft(target.getTime() - Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);
  return Math.max(0, left);
}

export function formatCountdown(ms: number): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hms = `${pad(Math.floor(totalSeconds / 3600) % 24)}:${pad(Math.floor(totalSeconds / 60) % 60)}:${pad(totalSeconds % 60)}`;
  return days > 0 ? `${days}d ${hms}` : hms;
}

export function formatKickoff(kickoff: Date, t: any): string {
  return kickoff.toLocaleString(t("locale"), { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function tileBackground(): string {
  if (window.theme === themeC.Orange) {
    return IsOrangeLight(window.theme)
      ? "linear-gradient(135.71deg, #FFF0E0 -0.16%, #FFFFFF 99.84%)"
      : "linear-gradient(135.71deg, #241D15 0%, #1E1E1E 55%)";
  }
  return "var(--thegame-header-background, var(--background_4))";
}

export function ProgressCells({ cells }: { cells: SxProps<Theme>[] }): ReactElement {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${cells.length}, minmax(0, 1fr))`, gap: "4px" }}>
      {cells.map((cellSx, i) => (
        <Box key={i} sx={{ height: 8, borderRadius: "4px", background: LINE, ...(cellSx as object) } as SxProps<Theme>} />
      ))}
    </Box>
  );
}

export function getPot(round: Pick<DetailedRound, "operatorTurnover" | "operatorStakePerRow" | "promotionalValues">, t: any, numMarkets: number): { label: string; text: string } {
  const currency = round.operatorTurnover?.currency ?? round.operatorStakePerRow.currency;
  const jackpot = getJackpot(currency, round as DetailedRound);
  return {
    label: jackpot ? GetJackpotString(window.theme, t, false, numMarkets) : t("turnover"),
    text: formatStringCurrency(jackpot ?? round.operatorTurnover?.amount ?? 0, currency, t, true),
  };
}

/* ------------------------------------------------------------------ */
/*  Live market state                                                 */
/* ------------------------------------------------------------------ */

const validSigns = ["ONE", "X", "TWO"];

// Share of all placed rows per coupon sign; undefined when the round has no row stats yet.
export function rowShares(fixture?: Fixture): Partial<Record<string, number>> | undefined {
  const signs = fixture?.signs;
  if (!signs || Object.keys(signs).length === 0) {
    return undefined;
  }
  const shares: Partial<Record<string, number>> = {};
  (["ONE", "X", "TWO"] as const).forEach((sign) => {
    const fraction = Number(signs[sign]?.rowsFraction);
    if (!Number.isNaN(fraction)) {
      shares[sign] = fraction;
    }
  });
  return shares;
}

export type MarketResult = "open" | "won" | "hit" | "miss" | "lost";

// Shallow rounds (landing) have no fixtures, so fixture status is optional input.
export interface LiveRoundData {
  status: string;
  fixtures?: Fixture[];
  preliminaryCorrectRow?: string[] | null;
  promotionalValues?: DetailedRound["promotionalValues"];
}

export interface MarketState {
  // Coupon sign (ONE/X/TWO) that is correct right now, if any.
  current?: string;
  decided: boolean;
  mine: string[];
  result: MarketResult;
  note?: string;
}

// THE_GAME_STANDINGS wins per market; markets it doesn't cover fall back to the preliminary row and fixture status.
function standingAt(standings: TheGameStandings | undefined, pos: number): { standing?: TheGameStandingsMarket; sign?: string } {
  const standing = standings?.markets.find((m) => m.pos === pos);
  return { standing, sign: standing ? toCouponSign(standing.sign) : undefined };
}

function currentSignAt(round: LiveRoundData, standings: TheGameStandings | undefined, pos: number): string | undefined {
  const prelim = round.preliminaryCorrectRow?.[pos];
  return standingAt(standings, pos).sign ?? (prelim && validSigns.includes(prelim) ? prelim : undefined);
}

export function getMarketStates(round: LiveRoundData, manifest: TheGameManifest, bets: Bet[]): MarketState[] {
  const numLegs = round.fixtures?.length || manifest.markets.length;
  const standings = getTheGameStandings(round);
  return Array.from({ length: numLegs }, (_, pos) => {
    const fixture = round.fixtures?.[pos];
    const market = manifest.markets.find((m) => m.pos === pos);
    const { standing, sign: standingSign } = standingAt(standings, pos);
    const current = currentSignAt(round, standings, pos);
    const decided =
      !!current &&
      (standingSign
        ? standing?.decided === true || round.status === "FINISHED"
        : market?.decided === true || fixture?.status === "FINISHED" || round.status === "FINISHED");
    const mine = Array.from(new Set(bets.flatMap((bet) => bet.legs?.[pos]?.signs ?? [])));
    const note = standing?.note ?? market?.note;

    let result: MarketResult = "open";
    if (current) {
      const isMine = mine.includes(current);
      result = decided ? (isMine ? "won" : "lost") : isMine ? "hit" : "miss";
    }
    return { current, decided, mine, result, note };
  });
}

// Mathematical systems contain every combination, so a bet's best row hits every leg that covers the current sign.
export function bestRowNow(round: LiveRoundData, bets: Bet[]): number {
  const standings = getTheGameStandings(round);
  return bets.reduce((best, bet) => {
    const hits = (bet.legs ?? []).filter((leg, pos) => {
      const current = currentSignAt(round, standings, pos);
      return !!current && leg.signs.includes(current);
    }).length;
    return Math.max(best, hits);
  }, 0);
}
